/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, RefObject } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import { DatatipContext, ItemInfo, TagCloudItemData } from './tagCloud.type';
import { useUser } from '#hooks/UNSAFE_useUser';
import { TagItemProps } from './TagCloudItem';
import { useDatatip } from '#hooks/PRIVATE_useDatatip';
import { calculateOffset } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { Property } from 'csstype';
import { DatatipConfig } from '#utils/UNSAFE_visTypes/common';

type Props<K, D> = {
  items: D[];
  touchResponse: 'touchStart' | 'touchHold';
  datatip?: DatatipConfig<DatatipContext<K, D>>;
  borderColor?: Property.BorderColor;
  rootRef: RefObject<HTMLDivElement>;
  focusedItemInfo: ItemInfo;
  hoveredItemInfo?: ItemInfo;
  focusedItemRef: RefObject<TagItemProps<K> | null>;
};

/**
 * Calculate the datatip offset for datatip when keyboard focus is active.
 * @param rootRef The ref for root of the tag cloud.
 * @param currentItem The ItemInfo of the current keyboard focused item.
 * @param isRtl The direction.
 * @param focusedItemRef The ref for currentItem node.
 * @returns
 */
const calculateTagcloudOffset = <K>(
  rootRef: RefObject<HTMLDivElement>,
  currentItem: ItemInfo,
  isRtl: boolean,
  focusedItemRef: RefObject<TagItemProps<K> | null>
) => {
  if (
    focusedItemRef?.current &&
    currentItem.isCurrent &&
    rootRef.current &&
    focusedItemRef?.current.x != null &&
    focusedItemRef?.current.w != null &&
    focusedItemRef?.current.y != null &&
    focusedItemRef?.current.h != null
  ) {
    const bounds = {
      x: focusedItemRef.current.x,
      y: focusedItemRef.current.y,
      width: focusedItemRef.current.w,
      height: focusedItemRef.current.h
    };

    const rootDims = rootRef.current.getBoundingClientRect();
    return calculateOffset(isRtl, rootDims.width, bounds);
  }
  // fallback to default
  return calculateOffset(isRtl, 0, undefined);
};

/**
 * Custom hook to manage datatip (tooltip) display for Tagcloud items.
 *
 * This hook calculates the appropriate offset and content for a datatip based on keyboard focus,
 * hover state, and legend structure. It supports both sectioned and flat legend data, RTL direction,
 * and touch interactions.
 *
 * @template K - Key type for TagCloudDataItem (string or number)
 * @template D - TagCloudDataItem data type
 * @returns {ReturnType<typeof useDatatip>} Datatip configuration for rendering
 */
export function useTagCloudDatatip<K extends string | number, D extends TagCloudItemData<K>>({
  items,
  touchResponse,
  datatip,
  rootRef,
  focusedItemRef,
  focusedItemInfo,
  hoveredItemInfo
}: Props<K, D>) {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const [offset, setOffset] = useState<{ mainAxis?: number; crossAxis?: number }>(
    calculateTagcloudOffset(rootRef, focusedItemInfo, isRtl, focusedItemRef)
  );

  useLayoutEffect(() => {
    setOffset(calculateTagcloudOffset(rootRef, focusedItemInfo, isRtl, focusedItemRef));
  }, [focusedItemInfo, focusedItemRef, isRtl, rootRef]);

  const anchor = focusedItemInfo?.isCurrent ? 'element' : 'pointer';
  const placement = 'top-start' as const;

  let content: ComponentChildren = undefined;
  let borderColor: string | undefined = undefined;
  let defaultContainer: 'enabled' | 'disabled' | undefined = undefined;

  const activeItemInfo = focusedItemInfo?.isCurrent
    ? focusedItemInfo
    : hoveredItemInfo?.isCurrent
    ? hoveredItemInfo
    : undefined;
  if (activeItemInfo) {
    const { idx } = activeItemInfo;
    const activeItem = items[idx];

    if (activeItem) {
      const detail: DatatipContext<K, D> = {
        data: activeItem,
        id: activeItem.id
      };
      const config = datatip?.(detail);
      if (config?.content !== '') {
        content = config?.content || activeItem.accessibleLabel;
        borderColor = config?.borderColor;
        defaultContainer = config?.defaultContainer;
      }
    }
  }

  const { datatipContent, datatipProps } = useDatatip({
    content,
    borderColor,
    defaultContainer,
    anchor,
    placement,
    offset: offset,
    touchResponse
  });
  return { datatipContent, datatipProps };
}
